import { driver } from "driver.js";

// El tour cruza rutas reales del SPA navegando con react-router, lo que desmonta y vuelve a
// montar el layout (AdminLayout/EmployeeLayout no son layouts de ruta anidada, cada página
// los renderiza por su cuenta) — por eso esta orquestación vive fuera de cualquier componente
// de React, en estado a nivel de módulo, para sobrevivir esos remounts intactos.
let activeDriverInstance = null;
let expectedNextPath = null;

// Si el usuario navega manualmente (sidebar, atrás del navegador, etc.) mientras el tour está
// activo Y ese cambio de ruta no fue el que el propio tour disparó, se cierra el paso actual
// en vez de dejar un popover apuntando a un elemento que ya no existe.
export function notifyExternalNavigation(pathname) {
  if (activeDriverInstance && pathname !== expectedNextPath) {
    activeDriverInstance.destroy();
  }
}

const isVisible = (el) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && window.getComputedStyle(el).visibility !== "hidden";
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForSelector(selector, timeoutMs) {
  if (!selector) return null;
  const start = Date.now();
  while (true) {
    const el = document.querySelector(selector);
    if (el && isVisible(el)) return el;
    if (Date.now() - start > timeoutMs) return null;
    // eslint-disable-next-line no-await-in-loop
    await sleep(80);
  }
}

// Resuelve los pasos de una sección contra el DOM real ya montado. Un paso cuyo elemento no
// existe o no es visible en esta pantalla/viewport (ej. el sidebar oculto en móvil) se omite
// silenciosamente en vez de romper el recorrido — el usuario ve un tour más corto, nunca uno roto.
async function resolveGroupSteps(group) {
  const resolved = [];
  for (const step of group.steps) {
    if (!step.target) {
      resolved.push({ popover: { title: step.title, description: step.description } });
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    const el = await waitForSelector(step.target, 1500);
    if (!el) continue;
    resolved.push({
      element: step.target,
      popover: { title: step.title, description: step.description, side: step.side, align: step.align || "start" },
    });
  }
  return resolved;
}

// Corre un tour guiado en vivo compuesto de varias "secciones" (una por pantalla real de la
// app). Cada sección es su propia instancia de driver.js; al llegar al último paso de una
// sección, en vez del botón "Listo" de driver.js se navega a la ruta de la siguiente sección
// y se arranca una nueva instancia ahí — así el tour cruza rutas reales del SPA en vivo, no
// solo un carrusel de capturas. Cerrar el tour en cualquier punto (X, overlay, Esc) detiene
// toda la secuencia de inmediato.
export function runProductTour(groups, navigate, { onFinish, onExit } = {}) {
  let cancelled = false;

  async function runGroup(index) {
    if (cancelled) return;
    if (index >= groups.length) {
      onFinish?.();
      return;
    }
    const group = groups[index];
    if (group.route && window.location.pathname !== group.route) {
      expectedNextPath = group.route;
      navigate(group.route);
      await sleep(300);
    }
    if (cancelled) return;
    const steps = await resolveGroupSteps(group);
    if (cancelled) return;
    if (steps.length === 0) {
      runGroup(index + 1);
      return;
    }

    const isLastGroup = index === groups.length - 1;
    let completedNaturally = false;
    const lastStep = steps[steps.length - 1];
    lastStep.popover.onNextClick = (_el, _step, opts) => {
      completedNaturally = true;
      opts.driver.destroy();
    };

    const d = driver({
      showProgress: true,
      progressText: "{{current}} de {{total}}",
      nextBtnText: "Siguiente",
      prevBtnText: "Atrás",
      doneBtnText: isLastGroup ? "Finalizar" : "Continuar",
      overlayOpacity: 0.55,
      stagePadding: 6,
      steps,
      onCloseClick: (_el, _step, opts) => {
        completedNaturally = false;
        opts.driver.destroy();
      },
      onDestroyed: () => {
        activeDriverInstance = null;
        if (cancelled) return;
        if (completedNaturally) runGroup(index + 1);
        else onExit?.();
      },
    });
    activeDriverInstance = d;
    d.drive();
  }

  runGroup(0);

  return () => {
    cancelled = true;
  };
}
